<script setup>
import { WdsBreadCrumbs, WdsMapPin, WdsPillActive, WdsPropertyCard, WdsSheet } from '@wds/components';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links.js';
import { commonValues } from '@/config/common';

// ### MIDDLEWARE TO VALIDATE ROUTE
definePageMeta({ middleware: 'validate-district' });

const {
  $tracking,
  $url,
} = useNuxtApp();
const {
  t,
  locale,
} = useI18n();

// ### COMPOSABLES
const route = useRoute();
const dateTime = useDateTime();
const { useRouteManager } = useRoutes();
const { useUrl } = useUrls();
const { appendThreePiQueryParams } = useThreePiQueryParams();
const { buildSeo } = generateSeo();
const {
  defineLocationSchema,
  defineFaqSchema,
  defineListOfPropertiesSchema,
  defineListOfPopularPlacesSchema,
}
  = generateSchemas();
const { useImage } = useImages();
const { useFormatter } = useFormatters();
const { useRating } = useRatings();
const { usePropertyList } = useProperties();
const {
  isBelowMediumScreen,
  isMediumScreen,
  isSmallOrMediumScreen,
} = useMq();

// ### COMPOSABLES - APIs
const { getDistrictLocationTranslatedById } = useApiTranslator();
const { getDistrictPageTags } = useApiSeo();
const { getCityDistricts } = useApiCities();
const { getAllCitiesByCountryName } = useApiCountries();

// ### STORES
const sessionStore = useSessionStore();
const locationStore = useLocationStore();
const languageStore = useLanguageStore();
const testingStore = useTestingStore();
const {
  createDomMarker,
  preloadMapMarkerSdk,
} = useMapMarker();
const searchStore = useSearchStore();
const sustainabilityStore = useSustainabilityStore();

// ### A/B TESTING FEATURE TOGGLES
const experimentCityMap = testingStore.hasEnabledFeature('static_cityMap');
const experimentNewSortOrder = testingStore.hasEnabledFeature('web_newSortOrder');
const experimentNewSortOrderParticipating = testingStore.hasEnabledFeature('web_newSortOrderIsUserParticipating');

// ### CONSTANTS INITIALIZATION
const locationKey = 'district';
const entryPageName = 'DISTRICT';
const gtmPageName = 'staticFab';
const gtmClassification = 'fab';
const segmentTracking = SEGMENT_PAGE_DATA.district;

const transcodeInterpolate = new TranscodeInterpolate();
const urlParams = route?.params || null;
const urlQuery = route?.query || null;

// ### VARIABLES INITIALIZATION
const urlPageNumber = Number.parseInt(urlParams?.pageNumber) || null;
const mapMarkers = ref([]);
const {
  mountMapPinApp,
  mountPropertyCardApp,
} = useCityMapVueApps(mapMarkers);
const showMap = ref(false);
const mapSheetIsReady = ref(false);

// ### ROUTE VALIDATION
const propertyTypeObj = useRouteManager.validatePropertyTypeParam(urlParams?.propertyType);
const propertyTypeKey = propertyTypeObj?.key;

const districtObj = await useRouteManager.validateDistrictParam(
  propertyTypeObj,
  urlParams.country,
  urlParams.city,
  urlParams.district,
  urlPageNumber,
);
const urlData = useRouteManager.getUrlData(
  urlParams.propertyType,
  urlParams.continent,
  urlParams.country,
  urlParams.city,
  null,
  null,
  null,
  null,
  null,
  urlParams.district,
  urlPageNumber,
);
useRouteManager.redirectRoute(urlData, urlQuery);

/// ### DISTRICT HREFLANGS
const urlTranslations = await getDistrictLocationTranslatedById(
  districtObj?.continentId,
  districtObj?.countryId,
  districtObj?.cityId,
  districtObj?.id,
);
const hrefUrlsRaw = useUrl.generateHrefLangForDistrict(urlTranslations, propertyTypeKey, urlPageNumber);

const countryPageUrl = useUrl.getCountryPageUrl({
  propertyTypeSlug: propertyTypeObj?.slug,
  urlFriendlyContinent: districtObj?.urlFriendlyContinent,
  urlFriendlyCountry: districtObj?.urlFriendlyCountry,
});

const districtPageUrl = useUrl.getDistrictPageUrl(
  {
    propertyTypeSlug: propertyTypeObj?.slug,
    urlFriendlyContinent: districtObj?.urlFriendlyContinent,
    urlFriendlyCountry: districtObj?.urlFriendlyCountry,
    urlFriendlyCity: districtObj?.urlFriendlyCity,
    urlFriendlyDistrict: districtObj?.urlFriendlyName,
  },
  {},
  urlPageNumber,
);

const pageCanonicalUrl = districtPageUrl;

const districtFullLocationInfo = {
  id: districtObj?.id,
  district: districtObj?.name,
  urlFriendlyDistrict: districtObj?.urlFriendlyName,
  city: districtObj?.city,
  cityId: districtObj?.cityId,
  urlFriendlyCity: districtObj?.urlFriendlyCity,
  country: districtObj?.country,
  countryId: districtObj?.countryId,
  urlFriendlyCountry: districtObj?.urlFriendlyCountry,
  continent: districtObj?.continent,
  continentId: districtObj?.continentId,
  urlFriendlyContinent: districtObj?.urlFriendlyContinent,
};

// ### DISTRICT HERO IMAGE
const districtHeroImageObj = {
  mobile: districtObj?.image?.mobile,
  tablet: districtObj?.image?.tablet,
  desktop: districtObj?.image?.desktop,
  heroImage: districtObj?.image?.heroImage,
};

// ### DISTRICT BREADCRUMBS
const crumbs = [
  {
    label: t('t_A11YBCTITLEPROPERTYTYPE', { PROPERTYTYPE: t(propertyTypeObj.transcode) }),
    title: t(propertyTypeObj.transcode),
    link: useUrl.getWorldPageUrl({ propertyTypeSlug: propertyTypeObj?.slug }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: districtObj.continent,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: districtObj.continent,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    link: useUrl.getContinentPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: districtObj?.urlFriendlyContinent,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: districtObj.country,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: districtObj.country,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    link: useUrl.getCountryPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: districtObj?.urlFriendlyContinent,
      urlFriendlyCountry: districtObj?.urlFriendlyCountry,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: districtObj.city,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: districtObj.city,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    link: useUrl.getCityPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: districtObj?.urlFriendlyContinent,
      urlFriendlyCountry: districtObj?.urlFriendlyCountry,
      urlFriendlyCity: districtObj?.urlFriendlyCity,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: districtObj.name,
      PROPERTYTYPE: t(propertyTypeObj.transcode),
    }),
    title: districtObj.name,
    link: '#',
  },
];

// ### DISTRICT PROPERTY LIST
const districtAllProperties = districtObj?.properties;
usePropertyList.setPropertyList(districtAllProperties);
const districtTopCheapestPropertiesPlaceholders = usePropertyList.getTopCheapestPropertiesForPlaceholders?.value || [];
const districtTopRankedPropertiesPlaceholders = usePropertyList.getTopRankedPropertiesForPlaceholders?.value || [];

// ### DISTRICT POPULAR PLACES  (AKA DISTRICTS)
const cityPopularPlaces = await getCityDistricts(propertyTypeKey, districtObj?.cityId);
const cityPopularPlacesList = cityPopularPlaces?.reduce((list, location) => {
  if (location.id !== districtObj.id) {
    const locationData = {
      propertyTypeSlug: propertyTypeObj.slug,
      urlFriendlyContinent: districtObj?.urlFriendlyContinent,
      urlFriendlyCountry: districtObj?.urlFriendlyCountry,
      urlFriendlyCity: districtObj?.urlFriendlyCity,
      urlFriendlyDistrict: location.urlFriendlyName,
    };

    list.push({
      id: location.id,
      name: location.name,
      label: location.name,
      title: location.name,
      url: useUrl.getDistrictPageUrl(locationData),
      urlFriendlyName: location.urlFriendlyName,
      totalPropertiesAvailable: location.totalPropertiesAvailable,
      propertyTypeSlug: propertyTypeObj.slug,
      pricesFromFormatted:
        location?.pricesFrom?.value > 0
          ? useFormatter.formatCurrency(location.pricesFrom.value, location.pricesFrom.currency)
          : null,
    });
  }
  return list;
}, []);

// ### OTHER LOCATIONS IN COUNTRY OF CITY
const countryTopCities = (await getAllCitiesByCountryName(propertyTypeKey, districtObj?.urlFriendlyCountry)) || [];
const cityOtherLocations = countryTopCities.cities
  .filter((topCity) => topCity?.id !== districtObj?.cityId)
  .map((topCity) => {
    const locationData = {
      propertyTypeSlug: propertyTypeObj.slug,
      urlFriendlyContinent: districtObj.urlFriendlyContinent,
      urlFriendlyCountry: districtObj.urlFriendlyCountry,
      urlFriendlyCity: topCity.urlFriendlyName,
    };
    return {
      id: topCity.id,
      name: topCity.name,
      label: topCity.name,
      title: topCity.name,
      url: useUrl.getCityPageUrl(locationData),
    };
  });

// ### DISTRICT SEO DATA
const seoData = await getDistrictPageTags(propertyTypeKey, districtObj?.id);
const seoExtraData = {
  canonicalUrl: pageCanonicalUrl,
  ogImage: useImage.getImage(districtHeroImageObj?.desktop) || '',
};
seoData.ogTitle = seoData.ogTitle || seoData.title;
seoData.ogDescription
  = (seoData.ogDescription || seoData.description).replace(/\r?\n|\r/g, '') || t('t_DEFAULTSEODESCRIPTION');
seoData.ogImage = districtHeroImageObj.mobile;
seoData.ogUrl = seoData.canonical || pageCanonicalUrl;
const tradReplacements = {
  LOCATIONNAME: districtObj.name,
  DISTRICT: districtObj.name,
  STATENAME: districtObj.name,
  CITY: districtObj.city,
  CHOSENCONTINENT: districtObj.name,
  CHOSENCOUNTRY: districtObj.name,
  CHOSENCITY: districtObj.name,
  COUNTRY: districtObj.country,
  COUNTRYSEARCHURL: countryPageUrl,
  CONTINENT: districtObj.continent,
  SERVERNAME: commonValues.HOSTELWORLD,
  PROPCOUNT: districtObj?.numberOfProperties,
  REVIEWCOUNT: districtObj?.numberOfReviews,
  REVIEWSCOUNT: districtObj?.numberOfReviews,
  LCPROPERTYTYPES: t(propertyTypeObj?.transcode),
  PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(propertyTypeObj?.transcode),
  YEAR: new Date().getFullYear(),
  ACTIVEFILTER: '',
};

// ### ADD DISTRICT TOP RANKED PROPERTIES TO PLACEHOLDER REPLACEMENTS
if (Array.isArray(districtTopRankedPropertiesPlaceholders) && districtTopRankedPropertiesPlaceholders.length) {
  Object.assign(tradReplacements, ...districtTopRankedPropertiesPlaceholders);
}

// ### ADD DISTRICTS TOP CHEAPEST PROPERTIES TO PLACEHOLDER REPLACEMENTS
if (Array.isArray(districtTopCheapestPropertiesPlaceholders) && districtTopCheapestPropertiesPlaceholders.length) {
  Object.assign(tradReplacements, ...districtTopCheapestPropertiesPlaceholders);
}

if (seoData?.faqContent && Object.keys(seoData?.faqContent).length) {
  Object.keys(seoData.faqContent).forEach((faqEntry) => {
    return seoData.faqContent[faqEntry]
      ? (seoData.faqContent[faqEntry] = transcodeInterpolate.interpolate(
        seoData.faqContent[faqEntry],
        tradReplacements,
        ['##', '##'],
      ))
      : (seoData.faqContent[faqEntry] = '');
  });
}
const seoMetaData = buildSeo.getMetaData(seoData, seoExtraData, tradReplacements, false, locationKey);

const hrefUrls = useUrl.alignHrefLangAlternatesWithCanonical(hrefUrlsRaw, seoMetaData?.canonicalUrl);

// ### ABOUT DISTRICT
const aboutDistrictDescription = $url.processHtmlContent(seoMetaData?.copy);

// ### COMPUTED
const getMainTitle = computed(() => {
  return (
    seoMetaData.h1
    || t('t_PROPERTYTYPEINLOCATION', {
      PROPERTYTYPE: t(propertyTypeObj.transcode),
      LOCATION: districtObj?.name,
    })
  );
});

const getPropertiesCountText = computed(() => {
  let translation = t('t_WEHAVEXHOSTELSINCITYSHORT', {
    PROPERTYTYPE: t(districtObj?.numberOfProperties === 1 ? propertyTypeObj.transcode_s : propertyTypeObj.transcode),
    NUMOFPROPERTIES: districtObj?.numberOfProperties,
    CITY: districtObj?.name,
  });

  if (districtObj.avgRating > 0) {
    translation = `${translation} ${t('t_RWDWEHAVEXHOSTELSINLOCATIONRATINGS', {
      AVGRATING: useFormatter.formatRating(districtObj.avgRating),
      NUMOFREVIEWS: districtObj.numberOfReviews,
    })}`;
  }
  return translation;
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isBelowMediumScreen) ? 2 : toValue(isMediumScreen) ? 3 : null;
});

const hasPopularPlaces = computed(() => {
  return cityPopularPlacesList?.length > 0;
});

const hasOtherLocations = computed(() => {
  return cityOtherLocations?.length > 0;
});

const getFaqContent = computed(() => {
  const faqContent = seoData?.faqContent || {};
  const newFaqContent = [];

  if (Object.keys(faqContent)) {
    Object.keys(faqContent).reduce((newFaqContent, faqKey) => {
      if (!faqKey.match(/ response$/) && !!faqContent[faqKey]) {
        const faqHeader = $url.processHtmlContent(faqContent[faqKey]);
        const faqResponse = $url.processHtmlContent(faqContent[`${faqKey} response`]);

        newFaqContent.push({
          header: faqHeader?.processedContent || faqContent[faqKey],
          content: faqResponse?.processedContent || faqContent[`${faqKey} response`],
        });
      }
      return newFaqContent;
    }, newFaqContent);
  }

  return newFaqContent;
});

const hasFaq = computed(() => {
  return getFaqContent?.value?.length > 0;
});

const hasProperties = computed(() => {
  return districtObj?.numberOfProperties > 0;
});

const isFirstPage = computed(() => {
  return districtObj?.page === 1;
});

const getMapConfigs = computed(() => {
  const cityMapCenter = {
    lng: districtObj?.cityGeoCoordinates?.longitude,
    lat: districtObj?.cityGeoCoordinates?.latitude,
  };

  return {
    scrollZoom: false,
    center: cityMapCenter,
    zoom: 6,
  };
});

const getMapButtonParams = computed(() => {
  const humanReadableSearchQuery
    = districtObj?.city && districtObj?.country
      ? `${districtObj.city}, ${districtObj.country}`
      : `${districtObj.urlFriendlyName}, ${districtObj.urlFriendlyCountry}`;

  const location = {
    cityId: districtObj.cityId,
    cityName: districtObj?.city,
    countryName: districtObj?.country,
    searchString: humanReadableSearchQuery,
  };

  return {
    text: t('t_MAP'),
    iconStart: 'map',
    iconEnd: experimentCityMap && !toValue(isSmallOrMediumScreen) && showMap.value ? 'close' : '',
    href: experimentCityMap
      ? ''
      : useUrl.getCityPageUrlDynamic(location, {
        district: districtObj.id,
        display: 'map',
      }),
  };
});

const getAppDownloadDetails = computed(() => {
  const searchParamsCheckIn = dateTime.formatToFlat(searchStore.getSearchCheckIn);
  const searchParamsCheckOut = dateTime.formatToFlat(searchStore.getSearchCheckOut);

  const searchParamsGroupType = searchStore.getSearchGroupType?.value || null;

  let appLink = `cy/${districtObj.cityId}?from=${searchParamsCheckIn}&to=${searchParamsCheckOut}&guests=${searchStore.getSearchNumberOfGuests}`;

  if (searchParamsGroupType) {
    appLink += `&groupType=${searchParamsGroupType}&groupAgeRange=${searchStore.getSearchGroupAge}`;
  }

  return {
    page: 'district',
    linkPaths: APPDOWNLOADBANNERLINKS?.DISTRICT,
    linkParams: appLink,
  };
});

// ### METHODS - PROPERTY LIST
function handleSustainabilityPinClick(propertySustainabilityInfo) {
  sustainabilityStore.setSustainabilityDetails(propertySustainabilityInfo);

  const trackingInfo = {
    gtm: {
      parameter1: `SUSTAINABILITY_TIER_${propertySustainabilityInfo?.tier}`,
      parameter2: gtmPageName,
    },
  };

  $tracking.onSustainabilityIconClick(trackingInfo.gtm);
}

// ### METHODS - DISTRICT MAP
function handleMapButtonClick() {
  if (experimentCityMap) {
    showMap.value = !showMap.value;
  }
}

function getMapPropertyUrl(property) {
  if (property?.type === '3PI') {
    const externalUrl = useUrl.get3PIPropertyPageUrl(property?.urlPath);
    return appendThreePiQueryParams(externalUrl, property, districtFullLocationInfo) || externalUrl;
  }

  const propertyTypeInfo = findPropertyTypeByKey(property.type);
  return useUrl.getPropertyPageUrl({
    propertyTypeSlug: t(propertyTypeInfo.slug),
    propertyId: property.id,
    urlFriendlyProperty: property.urlFriendlyName,
  });
}

function handlePinClick(property) {
  const propertyCardTemplate = defineComponent(WdsPropertyCard);

  const propertyUrl = getMapPropertyUrl(property);
  const propertyCardTarget = document.querySelector('.district-map-container .district-map-property-card');
  mountPropertyCardApp(propertyCardTemplate, {
    name: property.name,
    layout: 'compact',
    orientation: 'horizontal',
    href: propertyUrl,
    gallery: {
      slides: [useImage.getImage(property.image.small)],
      alt: property.name,
      title: property.name,
    },
    rating: {
      score: property.avgRating,
      totalReviews: property.numberReviews,
      translations: toValue(useRating.getRatingScoreKeywordTranslations),
    },
    fromPrice: { curPrice: property.minPriceString },
  }, propertyCardTarget);

  mapMarkers.value.forEach((m) => {
    const markerPin = m.pin;
    const isClickedMarker = markerPin.id === property.id;
    const propertyCard = document.querySelector(
      `.property-listing-cards .property-listing-card[data-pkey='p${markerPin.id}']`,
    );

    markerPin.getElement().querySelector('.pin-wrapper').classList.toggle('theme-blue', isClickedMarker);
    markerPin.getElement().style.zIndex = isClickedMarker ? 1 : 'initial';
    propertyCard?.classList?.remove('selected');

    if (isClickedMarker) {
      markerPin._map.panTo(m.pin.getLngLat());

      // Check if propertyCard exists and isSmallOrMediumScreen is false
      if (propertyCard && !isSmallOrMediumScreen.value) {
        propertyCard.classList.add('selected');

        // Use setTimeout to ensure that the browser has finished rendering before scrolling
        setTimeout(() => {
          propertyCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 0);
      }
    }
  });
}

async function buildPropertyMapPin(property) {
  if (import.meta.server) {
    return null;
  }

  const mapPinTemplate = defineComponent(WdsMapPin);
  const markerLongitude = Number(property?.longitude);
  const markerLatitude = Number(property?.latitude);

  if (!Number.isFinite(markerLongitude) || !Number.isFinite(markerLatitude)) {
    return null;
  }

  const markerContainer = document.createElement('div');

  mountMapPinApp(mapPinTemplate, {
    ratingScore: property.avgRating,
    price: property.minPriceString,
    onClick: () => handlePinClick(property),
  }, markerContainer);

  const newMarker = await createDomMarker({
    element: markerContainer,
    anchor: 'bottom',
    lngLat: [markerLongitude, markerLatitude],
  });

  if (!newMarker) {
    return null;
  }

  newMarker.id = property.id;
  return newMarker;
}

const MAP_MARKER_BATCH_SIZE = 20;

async function setupCityMapMarkers() {
  const cityPropertiesForMap = districtObj?.properties || [];
  const markerJobs = [];

  for (const property of cityPropertiesForMap) {
    const {
      id,
      latitude,
      longitude,
      sharedMinPrice,
      privateMinPrice,
    } = property;

    if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))
      || latitude == null || longitude == null) {
      continue;
    }

    const privateRoomsPrice = privateMinPrice?.value || 0;
    const dormRoomsPrice = sharedMinPrice?.value || 0;
    const propertyMinPrice = Math.min.apply(0, [privateRoomsPrice, dormRoomsPrice].filter(Number));

    if (propertyMinPrice === Infinity) {
      continue;
    }

    const propertyCurrency = sharedMinPrice?.currency || privateMinPrice?.currency;
    const propertyMinPriceFormatted = useFormatter.formatCurrency(propertyMinPrice, propertyCurrency);
    property.minPriceString = propertyMinPrice > 0 ? propertyMinPriceFormatted : '';
    markerJobs.push({
      id,
      latitude,
      longitude,
      property,
    });
  }

  await preloadMapMarkerSdk();

  const loadedMarkers = [];

  for (let index = 0; index < markerJobs.length; index += MAP_MARKER_BATCH_SIZE) {
    const batch = markerJobs.slice(index, index + MAP_MARKER_BATCH_SIZE);
    const batchMarkers = await Promise.all(batch.map(async (markerJob) => {
      const pin = await buildPropertyMapPin(markerJob.property);

      if (!pin) {
        return null;
      }

      return {
        id: markerJob.id,
        latitude: markerJob.latitude,
        longitude: markerJob.longitude,
        numProperties: 1,
        pin,
      };
    }));

    loadedMarkers.push(...batchMarkers.filter(Boolean));
  }

  mapMarkers.value = loadedMarkers;
}

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(hrefUrls),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### LOCATION
locationStore.setLocation(locationKey, {
  id: districtObj?.id,
  name: districtObj?.name,
  image: districtHeroImageObj,
});
locationStore.setLocation('city', {
  id: districtObj?.cityId,
  name: districtObj?.city,
  image: districtHeroImageObj,
});
locationStore.setLocation('country', {
  id: districtObj?.countryId,
  name: districtObj?.country,
  image: districtHeroImageObj,
});

// ### SCHEMAS
const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({
    '@id': '#hostelworldNavigation',
    'name': 'Breadcrumb',
    'itemListElement': schemaCrumbs,
  }),
  defineLocationSchema('TouristAttraction', districtObj, seoMetaData),
  defineFaqSchema(hasFaq.value ? getFaqContent?.value : null),
  defineListOfPropertiesSchema(districtAllProperties, districtObj, propertyTypeObj),
  defineListOfPopularPlacesSchema(cityPopularPlacesList, districtObj, propertyTypeObj, seoMetaData),
]);

// ### TRACKING
sessionStore.setEntryPoint(entryPageName);

// ## mounted
onMounted(async () => {
  showMap.value = !isSmallOrMediumScreen;
  if (experimentCityMap) {
    await setupCityMapMarkers();
  }

  const trackingPropertyType = propertyTypeObj?.type?.toLowerCase();
  const trackingContinentName = districtObj?.tracking?.continent;
  const trackingCountryName = districtObj?.tracking?.country;
  const trackingCityName = districtObj?.tracking?.city;
  const trackingDistrictName = districtObj?.tracking?.name;

  const top5PropertyCards = usePropertyList.getTop5PropertiesForTracking.value;

  const sortOrderExperimentVariation = experimentNewSortOrder ? 'variation' : 'control';

  const trackingInfo = {
    gtm: {
      'gtmPropertyType': trackingPropertyType,
      'gtmCanonicalUrl': seoMetaData?.canonicalUrl,
      'gtmContinent': trackingContinentName,
      'gtmCountry': trackingCountryName,
      'gtmCity': trackingCityName,
      gtmPageName,
      gtmClassification,
      'gtmArrivalDate': dateTime.formatToFlat(searchStore.getSearchCheckIn),
      'gtmArrivalDateStripped': dateTime.formatToFlat(searchStore.getSearchCheckIn).replace(/-/g, ''),
      'gtmDepartureDate': dateTime.formatToFlat(searchStore.getSearchCheckOut),
      'gtmDepartureDateStripped': dateTime.formatToFlat(searchStore.getSearchCheckOut).replace(/-/g, ''),
      'gtmSearchKeywords': searchStore.getSearchPhrase || '',
      'gtmNumberResults': districtObj?.numberOfProperties,
      'gtmFABAlgorithm': 'sfab',
      'gtmSortOrder': 'sfab',
      'gtmTop30Properties': usePropertyList.getFlatListOfTop30Properties?.value?.ids?.join('|') || '',
      'gtmPropertyIds': usePropertyList.getFlatListOfIds.value || 0,
      'gtmFABProperties6-10': usePropertyList.getFlatListOf6To10Ids.value || '',
      'gtmFABPropertyCard1': top5PropertyCards && top5PropertyCards[0] ? top5PropertyCards[0] : '',
      'gtmFABPropertyCard2': top5PropertyCards && top5PropertyCards[1] ? top5PropertyCards[1] : '',
      'gtmFABPropertyCard3': top5PropertyCards && top5PropertyCards[2] ? top5PropertyCards[2] : '',
      'gtmFABPropertyCard4': top5PropertyCards && top5PropertyCards[3] ? top5PropertyCards[3] : '',
      'gtmFABPropertyCard5': top5PropertyCards && top5PropertyCards[4] ? top5PropertyCards[4] : '',
    },
    segment: {
      event_name: segmentTracking.VIEWED,
      page_type: segmentTracking.TYPE,
      property_type: trackingPropertyType,
      destination_continent: trackingContinentName,
      destination_country: trackingCountryName,
      destination_city: trackingCityName,
      destination_city_id: districtObj?.cityId?.toString(),
      destination_district: trackingDistrictName,
      number_results: districtObj?.numberOfProperties,
      top_30_properties: usePropertyList.getFlatListOfTop30Properties?.value?.ids?.join('|') || '',
      top_30_privates_from_prices: usePropertyList.getFlatListOfTop30Properties?.value?.private_prices?.join('|') || '',
      top_30_dorms_from_prices: usePropertyList.getFlatListOfTop30Properties?.value?.dorms_prices?.join('|') || '',
      free_breakfast: usePropertyList.getFlatListOfTop30Properties?.value?.free_breakfast?.join('|') || '',
      free_wifi: usePropertyList.getFlatListOfTop30Properties?.value?.free_wifi?.join('|') || '',
      covid_icon: usePropertyList.getFlatListOfTop30Properties?.value?.covid_icon?.join('|') || '',
      hoscar_icon: usePropertyList.getFlatListOfTop30Properties?.value?.hoscar_icon?.join('|') || '',
      esg_label: usePropertyList.getFlatListOfTop30Properties?.value?.esg_label?.join('|') || '',
      auto_sort_order_applied: 'sfab',
      page_sort_order_applied: 'sfab',
      sfab_test_id_2: experimentNewSortOrderParticipating ? sortOrderExperimentVariation : 'non-participant',
    },
  };

  $tracking?.trackOnPageLoad(trackingInfo.gtm, trackingInfo.segment);
});
</script>

<script>
/* eslint-disable */
import { ca } from '@/lib/channelAttribution.js';

const entryPageNameJs = 'DISTRICT';

if (import.meta.client) {
  ca?.setChannelAttribution(null, entryPageNameJs);
}
/* eslint-enable */
</script>

<template>
  <ClientOnly>
    <LazyCommonHwAppDownloadBanner v-bind="getAppDownloadDetails" />
  </ClientOnly>

  <CommonHwHeader
    :headline="getMainTitle"
    :sub-label="getPropertiesCountText"
  />

  <section class="hw-wrapper headline-section">
    <WdsBreadCrumbs
      class="headline-breadcrumbs"
      :breadcrumbs="crumbs"
      :max-crumbs-shown="getCrumbsShownByViewport"
      :reversed="false"
    />
    <div class="headline-map">
      <WdsPillActive
        class="headline-map-button"
        v-bind="getMapButtonParams"
        @click="handleMapButtonClick"
      />
    </div>
  </section>

  <section
    class="hw-wrapper middle-section"
    :class="{ 'with-map': experimentCityMap && showMap }"
  >
    <CommonHwPropertyCardList
      v-if="hasProperties"
      class="property-list-container"
      :property-list="districtAllProperties"
      :total-pages="districtObj?.numberOfPages"
      :current-page="districtObj?.page || 1"
      :property-type-obj="propertyTypeObj"
      :location-info="districtFullLocationInfo"
      @sustainability-pin-click="handleSustainabilityPinClick"
    />
    <CommonHwSidebar
      v-if="!experimentCityMap || !showMap"
      class="sidebar-container"
    />
    <Client-Only>
      <div
        v-if="experimentCityMap && !isSmallOrMediumScreen && showMap"
        class="district-map-container"
      >
        <CommonHwMap
          :property-type-obj="propertyTypeObj"
          :map-config="getMapConfigs"
          :marker-list="mapMarkers"
          :apply-clusters="false"
        />
        <div class="district-map-property-card" />
      </div>
    </Client-Only>
  </section>

  <CommonHwPageContentSeo
    v-if="aboutDistrictDescription?.processedContent"
    class="hw-wrapper district-section"
    :content="aboutDistrictDescription?.processedContent"
    :title="$t('t_ABOUTLOCATION', { LOCATION: `<span>${districtObj.name}</span>` })"
  />

  <CityHwPopularPlaces
    v-if="hasPopularPlaces"
    class="hw-wrapper district-section"
    :property-type-obj="propertyTypeObj"
    :location-info="districtFullLocationInfo"
    :popular-places="cityPopularPlacesList"
    :title="$t('t_OTHERDISTRICTSTITLE', { LOCATIONNAME: `<span>${districtObj.name}</span>` })"
  />

  <section
    v-if="hasFaq || hasOtherLocations"
    class="hw-wrapper district-section faq-and-other-locations-section split-view"
  >
    <CommonHwFaq
      v-if="hasFaq && isFirstPage"
      :content="getFaqContent"
    />
    <CommonHwOtherLocationsInLocation
      v-if="hasOtherLocations"
      :location-name="districtObj?.country"
      :other-locations="cityOtherLocations"
      :property-type-obj="propertyTypeObj"
    />
  </section>

  <CommonHwLanguagePicker v-show="languageStore.isLanguagePickerOpen" />

  <WdsSheet
    v-if="experimentCityMap && isSmallOrMediumScreen && showMap"
    class="district-map-sheet"
    :is-open="showMap"
    :heading="$t('t_MAP')"
    :close-button-aria-label="$t('t_A11YSHEETCLOSE')"
    @sheet-close="mapSheetIsReady = showMap = false"
    @ready="mapSheetIsReady = true"
  >
    <template #sheetBody>
      <div class="district-map-container">
        <CommonHwMap
          :property-type-obj="propertyTypeObj"
          :map-config="getMapConfigs"
          :marker-list="mapMarkers"
          :apply-clusters="false"
        />
        <div class="district-map-property-card" />
      </div>
    </template>
  </WdsSheet>
</template>

<style lang="scss" scoped>
.district-section {
  margin: wds-rem(72px) auto;

  @include desktop {
    margin: wds-rem(120px) auto;
  }
}

.headline-section {
  display: flex;
  flex-direction: column;
  padding: 0 $wds-spacing-m $wds-spacing-s;

  .headline-map {
    background-image: url('~/public/map-horizontal.svg');
    background-repeat: no-repeat;
    background-position: top left;
    background-size: auto;
    padding: wds-rem(12px);
    display: flex;
    justify-content: center;
    border-radius: $wds-border-radius-m;
    box-shadow: inset 0 0 0 wds-rem(1px) $wds-color-ink-lighter;
  }

  @include desktop {
    flex-direction: row;
    gap: $wds-spacing-l;
    padding-left: $wds-spacing-xl;
    padding-right: wds-rem(40px);

    .headline-breadcrumbs {
      flex: 1;
    }

    .headline-map {
      width: wds-rem(384px);
      margin-inline: 0 !important;
    }
  }
}

.middle-section {
  position: relative;
  display: grid;
  grid-template-areas:
    'page-filters'
    'property-list';
  gap: $wds-spacing-m 0;
  margin-top: $wds-spacing-m;

  .filters-container {
    grid-area: page-filters;
  }

  .property-list-container {
    grid-area: property-list;
  }

  .sidebar-container {
    display: none;
  }

  .district-map-container {
    display: none;
  }

  @include desktop {
    grid-template-areas:
      'page-filters side-bar'
      'property-list side-bar';
    gap: $wds-spacing-m $wds-spacing-xxl;
    grid-template-columns: 1fr wds-rem(384px);

    &.with-map {
      grid-template-areas:
        'page-filters map'
        'property-list map';
      grid-template-columns: 1fr 50%;
    }

    .sidebar-container {
      display: block;
      grid-area: side-bar;
      padding-top: $wds-spacing-l;
    }

    .district-map-container {
      display: block;
      grid-area: map;
      height: calc(100vh - wds-rem(124px));
      top: wds-rem(104px);
      width: 100%;
      background-color: $wds-color-ink-lighter;
      position: sticky;
      border-radius: $wds-border-radius-l;
      overflow: hidden;
    }
  }
}

.faq-and-other-locations-section {
  display: grid;
  grid-auto-flow: row;
  grid-auto-columns: 1fr;
  gap: wds-rem(72px);
  align-items: flex-start;

  @include desktop {
    &.split-view {
      gap: wds-rem(40px);
      grid-auto-flow: column;
    }
  }
}

.district-map-sheet {
  :deep(sheet-body) {
    position: relative;
  }

  .district-map-container {
    display: block;
    grid-area: map;
    height: calc(100vh - wds-rem(124px));
    top: wds-rem(104px);
    width: 100%;
    background-color: $wds-color-ink-lighter;
    position: sticky;
    border-radius: $wds-border-radius-l;
    overflow: hidden;
  }
}

.district-map-property-card {
  position: absolute;
  bottom: $wds-spacing-m;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  width: wds-rem(328px) !important;
}
</style>
